/**
 * Conteúdo do estilo PERGUNTAS (perguntas e diário guiado).
 *
 * Três cuidados valem para tudo aqui:
 *
 * 1. o **texto é original**, escrito para este produto — nada copiado de livro,
 *    site, teste de revista ou caixinha de pergunta;
 * 2. a pergunta é **aberta e concreta**: pergunta de sim ou não não rende página,
 *    e pergunta genérica ("o que é a felicidade?") trava a mão na hora de
 *    escrever;
 * 3. nada aqui é conselho de saúde, diagnóstico ou promessa. É convite para
 *    olhar a própria vida e anotar — quem lê decide o que fazer com isso.
 *
 * Cada entrada serve dois quadros do dia: `PERGUNTA_DO_DIA` (o tema, a pergunta e
 * o porquê dela) e `DIARIO_GUIADO` (o passo de escrita, com as linhas para
 * responder). O dia do miolo escolhe a entrada por
 * `índice % PERGUNTAS.length`, então o mesmo período reimpresso traz exatamente
 * as mesmas perguntas.
 */
export const PERGUNTAS = [
    {
        tema: 'Manhã',
        pergunta: 'Que parte da sua manhã você já sabe que vai dar certo?',
        aprofunda: 'Começar pelo que é previsível dá chão para o resto do dia.',
        passo: 'Descreva a sua primeira meia hora de hoje, do jeito que ela realmente aconteceu.',
    },
    {
        tema: 'Casa',
        pergunta: 'Qual canto da casa você evita sem perceber?',
        aprofunda: 'O lugar que a gente adia quase sempre guarda uma tarefa pequena.',
        passo: 'Vá até esse canto, olhe por um minuto e escreva o que ele está pedindo.',
    },
    {
        tema: 'Cheiros',
        pergunta: 'Qual cheiro te leva de volta para a casa de alguém?',
        aprofunda: 'O cheiro guarda lembranças que a vista já esqueceu.',
        passo: 'Descreva a cena inteira: onde era, quem estava, que horas eram.',
    },
    {
        tema: 'Trabalho',
        pergunta: 'Qual tarefa você faz bem e não custa nada — e quase ninguém nota?',
        aprofunda: 'O que sai fácil costuma ser talento, e talento passa batido por ser fácil.',
        passo: 'Liste três coisas que você faz sem esforço e alguém agradeceu uma vez.',
    },
    {
        tema: 'Amizade',
        pergunta: 'De quem você sente falta e não disse nada?',
        aprofunda: 'Falta de notícia quase nunca é falta de vontade — é falta de hora marcada.',
        passo: 'Escreva o nome e a primeira frase que você diria a essa pessoa.',
    },
    {
        tema: 'Tempo',
        pergunta: 'Em que momento de ontem o tempo passou rápido demais?',
        aprofunda: 'O que some das horas é o que interessa de verdade.',
        passo: 'Anote em que você estava e com quem, antes de esquecer.',
    },
    {
        tema: 'Corpo',
        pergunta: 'Onde o seu corpo avisa que você passou do ponto?',
        aprofunda: 'Ombro, nuca, mandíbula: cada um reclama do seu jeito, sempre no mesmo lugar.',
        passo: 'Faça uma pausa de dois minutos e escreva o que você sentiu nesse lugar.',
    },
    {
        tema: 'Escolhas',
        pergunta: 'Qual decisão você já tomou e continua pedindo opinião?',
        aprofunda: 'Quando a resposta já está pronta, perguntar de novo é adiar.',
        passo: 'Escreva a decisão e a resposta que você daria se ninguém fosse saber.',
    },
    {
        tema: 'Dinheiro',
        pergunta: 'Qual gasto pequeno você repete e nem conta?',
        aprofunda: 'O gasto invisível é o que pesa: ele passa tantas vezes que vira paisagem.',
        passo: 'Anote os gastos de hoje, até o café e o estacionamento.',
    },
    {
        tema: 'Medo',
        pergunta: 'Do que você tinha medo um ano atrás e hoje nem lembra?',
        aprofunda: 'Ver o medo antigo de fora é o jeito mais barato de perder um medo novo.',
        passo: 'Escreva uma coisa que você evitava e hoje faz sem pensar.',
    },
    {
        tema: 'Leitura',
        pergunta: 'Que frase você leu e ficou repetindo na cabeça?',
        aprofunda: 'A frase que fica é a que diz algo que você ainda não sabia dizer.',
        passo: 'Copie a frase, com o nome de quem escreveu, e conte onde ela apareceu.',
    },
    {
        tema: 'Música',
        pergunta: 'Qual música te devolve para uma idade específica?',
        aprofunda: 'A música marca a época melhor que a foto: a foto mostra, a música traz de volta.',
        passo: 'Escreva a idade, o lugar e com quem você ouvia.',
    },
    {
        tema: 'Cozinha',
        pergunta: 'Que prato você sabe fazer de olhos fechados?',
        aprofunda: 'Receita de cor é receita de casa: chegou por alguém, e você continua.',
        passo: 'Anote os ingredientes e de quem você aprendeu.',
    },
    {
        tema: 'Cidade',
        pergunta: 'Qual lugar da sua cidade você levaria alguém que nunca veio?',
        aprofunda: 'A gente para de ver o que é perto. Escolher um lugar é voltar a olhar.',
        passo: 'Descreva o lugar e a melhor hora para chegar lá.',
    },
    {
        tema: 'Trabalho em equipe',
        pergunta: 'O que você faz que só dá certo por causa de outra pessoa?',
        aprofunda: 'Ninguém trabalha sozinho de verdade, e o crédito é maior quando é dividido.',
        passo: 'Escreva o nome dela e o que muda quando ela está junto.',
    },
    {
        tema: 'Descanso',
        pergunta: 'O que te descansa de verdade, e não é dormir?',
        aprofunda: 'Descanso não é só pausa: é trocar de esforço.',
        passo: 'Liste o que te deixou mais leve nos últimos sete dias.',
    },
    {
        tema: 'Aprendizado',
        pergunta: 'O que você entendeu só depois de errar?',
        aprofunda: 'O erro explica melhor que o acerto, porque ele tem motivo.',
        passo: 'Conte o erro em três frases, sem se culpar e sem se defender.',
    },
    {
        tema: 'Rotina',
        pergunta: 'Qual hábito pequeno mudou seus dias de verdade?',
        aprofunda: 'Mudança que dura é menor do que parece e mais repetida do que se imagina.',
        passo: 'Anote o hábito, quando ele entrou e o que mudou desde então.',
    },
    {
        tema: 'Família',
        pergunta: 'Que história da sua família só você ainda conta?',
        aprofunda: 'História que ninguém repete desaparece junto com quem lembra.',
        passo: 'Escreva a história como você a ouviu, com as palavras daquela pessoa.',
    },
    {
        tema: 'Vizinhos',
        pergunta: 'Quem mora perto e você cumprimenta só de longe?',
        aprofunda: 'Vizinhança é a rede mais próxima e a mais esquecida.',
        passo: 'Escreva o nome de quem mora ao lado e uma coisa que você sabe da vida dela.',
    },
    {
        tema: 'Elogios',
        pergunta: 'Qual elogio você recebeu e não acreditou?',
        aprofunda: 'O elogio que a gente descarta costuma apontar o que os outros veem primeiro.',
        passo: 'Anote o elogio e quem disse, e depois escreva por que ele poderia ser verdade.',
    },
    {
        tema: 'Prioridades',
        pergunta: 'O que você fez hoje que não estava na sua lista?',
        aprofunda: 'A lista mostra a intenção; o dia mostra a escolha.',
        passo: 'Separe o dia em duas colunas: o que estava previsto e o que apareceu.',
    },
    {
        tema: 'Silêncio',
        pergunta: 'Quando foi a última vez que você ficou sem som nenhum por dez minutos?',
        aprofunda: 'Barulho de fundo não é companhia, e cansa mais do que parece.',
        passo: 'Anote em que momento do dia o silêncio caberia na sua rotina.',
    },
    {
        tema: 'Roupas',
        pergunta: 'Qual peça você veste quando precisa de coragem?',
        aprofunda: 'O que se veste muda como a gente entra no lugar.',
        passo: 'Descreva a peça, de onde ela veio e o que você já fez vestindo ela.',
    },
    {
        tema: 'Viagem',
        pergunta: 'Qual viagem você faria de novo, exatamente igual?',
        aprofunda: 'Repetir não é falta de imaginação: é reconhecer o que fez bem.',
        passo: 'Escreva três coisas que você faria de novo e uma que não faria.',
    },
    {
        tema: 'Talento',
        pergunta: 'O que você faria de graça e alguém pagaria para ver pronto?',
        aprofunda: 'A fronteira entre passatempo e ofício é feita de vizinhos curiosos.',
        passo: 'Liste três coisas que você faz por gosto e alguém já pediu.',
    },
    {
        tema: 'Redes',
        pergunta: 'O que você aprendeu na internet e usou na vida real?',
        aprofunda: 'É pouco, mas é o que ficou — e vale saber o que ficou.',
        passo: 'Anote uma coisa que você aprendeu ali e faz até hoje.',
    },
    {
        tema: 'Cidade natal',
        pergunta: 'O que só existe onde você nasceu?',
        aprofunda: 'Aprendemos a chamar de normal o que é só nosso.',
        passo: 'Escreva uma comida, uma palavra e um costume de lá.',
    },
    {
        tema: 'Generosidade',
        pergunta: 'Quem te ajudou e nunca cobrou por isso?',
        aprofunda: 'Ajuda de graça é o que sustenta a gente nos anos difíceis.',
        passo: 'Escreva o nome, o que ela fez e como isso chegou até hoje.',
    },
    {
        tema: 'Limites',
        pergunta: 'Que pedido você aceitou e não devia?',
        aprofunda: 'Dizer sim por educação quase sempre sai caro para quem disse.',
        passo: 'Escreva como você responderia hoje, com as palavras exatas.',
    },
    {
        tema: 'Primeiras vezes',
        pergunta: 'Qual foi o primeiro salário e no que ele foi gasto?',
        aprofunda: 'A primeira compra conta o que a gente achava que valia a pena.',
        passo: 'Anote o valor, a compra e o que você sentiu na hora.',
    },
    {
        tema: 'Bairro',
        pergunta: 'O que fechou no seu bairro e você ainda sente falta?',
        aprofunda: 'Sentir falta de um lugar é sentir falta de uma época.',
        passo: 'Escreva o nome do lugar, o que vendia e quem atendia.',
    },
    {
        tema: 'Estudo',
        pergunta: 'Qual assunto você estudaria se ninguém fosse cobrar?',
        aprofunda: 'O que a gente estudaria por vontade mostra para onde a curiosidade puxa.',
        passo: 'Anote o assunto e a primeira coisa que você procuraria saber.',
    },
    {
        tema: 'Nomes',
        pergunta: 'De quem você carrega o nome, ou uma parte dele?',
        aprofunda: 'Nome dado é escolha de alguém, e quase sempre tem história.',
        passo: 'Escreva o que você sabe sobre a escolha do seu nome.',
    },
    {
        tema: 'Conversas',
        pergunta: 'Qual conversa você precisou ter e ainda não teve?',
        aprofunda: 'Conversa adiada não some: ela volta em outro tom.',
        passo: 'Escreva as duas primeiras frases que você diria hoje.',
    },
    {
        tema: 'Orgulho',
        pergunta: 'O que você aprendeu a fazer sozinho?',
        aprofunda: 'O que ninguém ensinou tem um valor diferente: foi conquistado sem aula.',
        passo: 'Anote o que você aprendeu, quando e por que precisou.',
    },
    {
        tema: 'Presente',
        pergunta: 'Qual presente você deu e nunca soube se acertou?',
        aprofunda: 'Acertar de verdade é lembrar do que a pessoa já dizia antes do aniversário.',
        passo: 'Escreva o presente e o que você sabe que ela gostaria de receber.',
    },
    {
        tema: 'Números',
        pergunta: 'Que número da sua vida você nunca vai esquecer?',
        aprofunda: 'Telefone antigo, número da casa, matrícula: a memória guarda o que usou muito.',
        passo: 'Anote o número e conte quem ou o que ele identificava.',
    },
    {
        tema: 'Trabalho antigo',
        pergunta: 'O que o seu primeiro trabalho te ensinou sobre pessoas?',
        aprofunda: 'O primeiro emprego ensina mais sobre gente do que sobre a função.',
        passo: 'Escreva uma cena daquele tempo, com nome e lugar.',
    },
    {
        tema: 'Conviver',
        pergunta: 'O que você sabe da rotina de quem mora com você?',
        aprofunda: 'Conviver não é conhecer: os horários e os gostos contam quem são.',
        passo: 'Anote três detalhes do dia de alguém da sua casa, com hora e ordem.',
    },
];
//# sourceMappingURL=perguntas.js.map